import { useContextMenu } from '@/common/contextMenu/useContextMenu';
import { useDialog } from '@/common/dialog/DialogContext';
import { useEntityChangeSubscr } from '@/common/entityChange/EntityChangeContext';
import { EntityFormDialog } from '@/common/entityFramework/entityForm/EntityFormDialog';
import { ConfirmDeleteDialog } from '@/common/entityFramework/entityList/ConfirmDeleteDialog';
import { EntityFormMode } from '@/common/entityFramework/enumerations/EntityFormMode';
import { Mode } from '@/common/entityFramework/enumerations/Mode';
import { useNavState } from '@/common/navState/NavStateContext';
import { usePrincipal } from '@/common/principal/PrincipalContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { EntityPropForm } from './EntityPropForm';
import { buildPropIdFilter } from './propIdFilterUtils';

// Compact Property table for nesting inside another Entity's card (e.g. a Program Product's Tiers), styled to match
// that sibling list so Properties never look more prominent than the more important child list it sits alongside.
// showAddButton=false lets the caller render its own "Add property" trigger elsewhere (e.g. next to "Add tier").
export const EntityPropTable = ({ parentEntity, parentEntityObj, canEdit, typeFilterValues, showAddButton = true }) => {
  const rsrc = useRsrc();
  const { openDialog } = useDialog();
  const { navigateTo } = useNavState();
  const { hasRole } = usePrincipal();
  const { openContextMenu, contextMenu } = useContextMenu();

  const [rows, setRows] = useState(null);

  const canSave = canEdit && hasRole('MD_ENTITY_PROP_SAVE');
  const canDelete = canEdit && hasRole('MD_ENTITY_PROP_DELETE');

  const { refreshTrigger } = useEntityChangeSubscr('md', 'ENTITY_PROP');

  useEffect(() => {
    if (!parentEntityObj?.id) return;
    rsrc
      .rows('md', 'ENTITY_PROP', {
        expr: ExprUtils.whereParams('entity', parentEntity, 'entityId', parentEntityObj.id),
      })
      .then((res) => setRows(res ?? []));
  }, [parentEntity, parentEntityObj?.id, refreshTrigger]);

  const filterValues = (typeFilterValues ?? []).filter((value) => value != null);
  const propIdFilter = buildPropIdFilter(filterValues);

  const openPropDialog = (mode, entityProp) => {
    const initialValues =
      mode === EntityFormMode.ADD ? { entity: parentEntity, entityId: parentEntityObj.id } : undefined;

    openDialog(EntityFormDialog, {
      title: `${mode === EntityFormMode.ADD ? 'Add' : 'Edit'} Property — ${parentEntityObj.code}`,
      module: 'md',
      entity: 'ENTITY_PROP',
      mode,
      entityId: entityProp?.id,
      initialValues,
      maxWidth: 'xl',
      renderForm: (formProps) => (
        <EntityPropForm ownerObj={parentEntityObj} propIdFilter={propIdFilter} {...formProps} />
      ),
    });
  };

  const contextMenuItems = (row) => {
    const items = [
      {
        name: 'Details',
        icon: fas.faInfoCircle,
        action: () => navigateTo('md/entity-prop', { mode: Mode.DETAIL, entityId: row.id }),
      },
    ];
    if (canDelete) {
      items.push({
        name: 'Delete',
        icon: fas.faTrash,
        disabled: row.status === 'APPROVED' || row.status === 'APPROVING',
        action: () => openDialog(ConfirmDeleteDialog, { data: { module: 'md', entity: 'ENTITY_PROP', id: row.id } }),
      });
    }
    return items;
  };

  if (rows == null) return null;

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">Properties</Typography>
      {rows.length === 0 ?
        <Typography variant="body2" color="text.secondary">
          No properties yet.
        </Typography>
      : <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Property</TableCell>
              <TableCell>Value</TableCell>
              {canEdit && <TableCell align="right" sx={{ width: 56 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                sx={canSave ? { cursor: 'pointer' } : undefined}
                onClick={canSave ? () => openPropDialog(EntityFormMode.EDIT, row) : undefined}
                onContextMenu={(event) => openContextMenu(event, contextMenuItems(row))}
              >
                <TableCell>{row.propName ?? row.propCode}</TableCell>
                <TableCell>{row.value}</TableCell>
                {canEdit && (
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {canSave && (
                      <Tooltip title="Edit property">
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            openPropDialog(EntityFormMode.EDIT, row);
                          }}
                        >
                          <FontAwesomeIcon icon={fas.faPencil} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      }
      {canSave && showAddButton && (
        <Box>
          <Button
            size="small"
            startIcon={<FontAwesomeIcon icon={fas.faPlus} />}
            onClick={() => openPropDialog(EntityFormMode.ADD)}
          >
            Add property
          </Button>
        </Box>
      )}
      {contextMenu}
    </Stack>
  );
};
